import React, { useState, useEffect } from 'react';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  TextField,
  CircularProgress,
  Pagination,
  CardMedia,
  Chip,
} from '@mui/material';
import { LocationOn, Event } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const PortalSales = () => {
  const navigate = useNavigate();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchSales();
  }, [page, search]);

  const fetchSales = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: '12',
      });

      if (search) {
        params.append('search', search);
      }

      const response = await api.get(`/portal/sales?${params.toString()}`);

      setSales(response.data.sales || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to fetch sales:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Upcoming Estate Sales
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Browse our upcoming estate sales and find unique treasures
      </Typography>

      <TextField
        fullWidth
        placeholder="Search sales by name, city, or location..."
        value={search}
        onChange={handleSearchChange}
        sx={{ mb: 3 }}
      />

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={3}>
            {sales.map((sale) => (
              <Grid item xs={12} sm={6} md={4} key={sale.id}>
                <Card
                  sx={{
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': { transform: 'translateY(-4px)' },
                  }}
                  onClick={() => navigate(`/portal/sales/${sale.id}`)}
                >
                  {sale.primary_photo ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={`${process.env.REACT_APP_API_URL || 'https://api.paceestate.com'}${sale.primary_photo.file_path}`}
                      alt={sale.name}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 200,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography color="text.secondary">No image</Typography>
                    </Box>
                  )}
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {sale.name}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {sale.city}, {sale.state}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
                      <Event fontSize="small" color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {new Date(sale.start_date).toLocaleDateString()} -{' '}
                        {new Date(sale.end_date).toLocaleDateString()}
                      </Typography>
                    </Box>

                    <Chip
                      label={sale.status}
                      color={sale.status === 'published' ? 'success' : 'default'}
                      size="small"
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {sales.length === 0 && (
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography color="text.secondary">
                No sales found. Try adjusting your search.
              </Typography>
            </Box>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PortalSales;
